import { Injectable } from '@angular/core';
import { ConfigService } from "./config.service";
import { Observable } from "rxjs";
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";


export interface PagedResult<T> {
  totalCount: number
  totalPages: number
  pageIndex: number
  pageSize: 25
  results: T[]
}

export interface PayloadBag<T> {
  data: T
}

export interface ClaimDef {
  id: string
  claim: string
  value?: string
  description?: string
}


export interface UserDef {
  isPendingNew?: boolean
  id: string
  description?: string
  isSystem: boolean
  claims?: ClaimDef[]
  tenants?: string[]
}


export interface UserClaim {
  type: string
  value: string | number | boolean | null
}

export type ClaimTypeAndValue = {
  type: string
  value?: any
}

export interface SimpleReference {
  uid: string | number
  name: string | null
}

export interface UserListItem {
  id: string
  system: boolean
  claims: UserClaim[]
  tenants: SimpleReference[]
  description: string | null
}

export interface UserDetails extends UserListItem {
}

export interface UserCreateOrUpdate {
  id: string
  system: boolean
  claims: UserClaim[]
  tenants: string[]
  description: string | null
}

export interface OverviewMetrics {
  countTotal: number
  countByUser?: number | null
}

export interface TenantListItem {
  id: string
  name: string
  description?: string | null
  userCount: number
  assistantCount: number
}


export interface RagSearchConfig {
  disabled: boolean
  ragMinSearchScore?: number | null
  searchConfig: RagSimilaritySearchConfig
}

export interface RagSimilaritySearchConfig {
  limit?: number
  metric_type?: 'IP'
  output_fields?: string[]
}

export interface RagSimilaritySearchCollectionResult {
  collection: string
  matches: any[]
}

export interface ChatbotSettings {
  useSessionHistory: boolean
  maxSessionHistorySize: number
  ragSearch: { [id: string]: RagSearchConfig }
}

export interface ChatbotSettingsUpdate {
  persistent: boolean
  settings: ChatbotSettings
}

export interface ChatbotRagVariableInfo {
  schemaDef: { [id: string]: any }
  stats: { [id: string]: any}
  state: string,
  indexes: { [id: string]: any}
}

export interface ChatbotRagContextResult {
  variable: string
  exists: boolean
  info?: ChatbotRagVariableInfo | null
}

export interface IntentField {
  fieldType: 'string' | 'integer' | 'float'
  required: boolean
  default?: string | number | null
}

export type IntentFieldSchema = {[id: string]: IntentField}

export interface IntentDescriptor {
  title: string
  description: string
  inputSchema?: IntentFieldSchema | null
  outputSchema?: IntentFieldSchema | null
  handler?: string | null
}

export interface ChatbotDef {
  id: string
  name: string
  description: string
  tenant: string
  contextVars: string[]
  settings: ChatbotSettings
  intents?: IntentDescriptor[]
}

export interface RagSessionDef {
  id?: string
  createdBy?: string
  name: string
  chatbot?: string
  description?: string
}

export interface RagTemplateMessage {
  role: 'system' | 'user'
  message: string
}

export interface RagTemplateDef {
  id?: string
  assistantId: string
  createdBy: string
  name: string
  messages?: RagTemplateMessage[]
}

export interface RagFilterTemplate {
  filterName?: string
}

export interface ChatMessageRequest {
  id: string
  message: string
}

export interface ChatMessage {
  id: string
  refId: string | null
  type: 'human' | 'system'
  timestamp: string
  sender: string
  message: string
  stats?: {
    intentState?: any
    ragResults?: {[id: string] : any}[]
    tokenUsage?: {[id: string] : any}
    [id: string]: any
  }
  [id: string]: any
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  get baseUri(): string {
    const backendUri = this.configService.getConfig().backendUri
    if (backendUri.endsWith('/')) {
      console.warn('Normalizing backend URI. Trailing / are not allowed.')
      return backendUri.substring(0, backendUri.length-1)
    }
    return backendUri
  }

  constructor(
    private http: HttpClient,
    private configService: ConfigService
  ) {
  }

  httpGet<T>(path: string,
             options?: {
    headers?: HttpHeaders | {
      [header: string]: string | string[];
    };
    context?: HttpContext;
    observe?: 'body';
    params?: HttpParams | {
      [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
    };
    reportProgress?: boolean;
    responseType: 'json';
    withCredentials?: boolean;
    transferCache?: {
      includeHeaders?: string[];
    } | boolean;
  }): Observable<T> {
    path = `${this.baseUri}/${path}`
    return this.http.get<T>(path, options || { responseType: 'json'} )
  }

  httpDelete<T>(path: string,
             options?: {
               headers?: HttpHeaders | {
                 [header: string]: string | string[];
               };
               context?: HttpContext;
               observe?: 'body';
               params?: HttpParams | {
                 [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
               };
               reportProgress?: boolean;
               responseType: 'json';
               withCredentials?: boolean;
               transferCache?: {
                 includeHeaders?: string[];
               } | boolean;
             }): Observable<T> {
    path = `${this.baseUri}/${path}`
    return this.http.delete<T>(path, options || { responseType: 'json'} )
  }

  httpPost<TRequest, TResponse>(path: string,
                                payload: TRequest,
                                options?: {
                                  headers?: HttpHeaders | {
                                    [header: string]: string | string[];
                                  };
                                  context?: HttpContext;
                                  observe?: 'body';
                                  params?: HttpParams | {
                                    [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
                                  };
                                  reportProgress?: boolean;
                                  responseType?: 'json';
                                  withCredentials?: boolean;
                                }): Observable<TResponse> {
    path = `${this.baseUri}/${path}`
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
    options = options || {
      headers: headers
    }
    return this.http.post<TResponse>(path, JSON.stringify(payload), options)
  }

  httpPut<TRequest, TResponse>(path: string,
                               payload: TRequest,
                               options?: {
                                 headers?: HttpHeaders | {
                                   [header: string]: string | string[];
                                 };
                                 context?: HttpContext;
                                 observe?: 'body';
                                 params?: HttpParams | {
                                   [param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
                                 };
                                 reportProgress?: boolean;
                                 responseType?: 'json';
                                 withCredentials?: boolean;
                               }): Observable<TResponse> {
    path = `${this.baseUri}/${path}`
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
    options = options || {
      headers: headers
    }
    return this.http.put<TResponse>(path, JSON.stringify(payload), options)
  }

  getChatbotsOverview(): Observable<OverviewMetrics> {
    return this.httpGet('assistants/overview')
  }

  getChatbots(): Observable<ChatbotDef[]> {
    return this.httpGet('assistants')
  }

  getChatbot(uuid: string): Observable<ChatbotDef> {
    return this.httpGet(`assistants/${uuid}`)
  }

  postChatbotContextSearch(uuid: string, prompt: string): Observable<RagSimilaritySearchCollectionResult[]> {
    return this.httpPost<PayloadBag<string>, RagSimilaritySearchCollectionResult[]>(
      `assistants/${uuid}/context/search`, {
      data: prompt
    })
  }

  getChatbotContextData(uuid: string, contextVar: string, pageIndex: number = 0): Observable<PagedResult<any>> {
    if (pageIndex == 0) {
      return this.httpGet<PagedResult<any>>(`assistants/${uuid}/data/${contextVar}`)
    }
    return this.httpGet<PagedResult<any>>(`assistants/${uuid}/data/${contextVar}/${pageIndex}`)
  }

  createChatbotTrainData(uuid: string, contextVar: string, data: any): Observable<PayloadBag<string>> {
    return this.httpPut<any, PayloadBag<string>>(`assistants/${uuid}/data/${contextVar}`, data)
  }

  getChatbotContextVarInfo(uuid: string, contextVar: string): Observable<ChatbotRagContextResult> {
    return this.httpGet<ChatbotRagContextResult>(`assistants/${uuid}/context/${contextVar}`)
  }

  deleteChatbotContextVarEntry(uuid: string, contextVar: string, itemId: string): Observable<any> {
    return this.httpDelete<any>(`assistants/${uuid}/context/${contextVar}/items/${itemId}`)
  }

  postChatbotContextVarSearch(uuid: string, contextVar: string, prompt: string): Observable<any> {
    return this.httpPost<PayloadBag<string>, any>(`assistants/${uuid}/context/${contextVar}/search`, {
      data: prompt
    })
  }

  setChatbotSettings(uuid: string, settings: ChatbotSettingsUpdate): Observable<any> {
    return this.httpPut<ChatbotSettingsUpdate, any>(`assistants/${uuid}/settings`, settings)
  }

  getSessionsOverview(): Observable<OverviewMetrics> {
    return this.httpGet('sessions/overview')
  }

  getSessions(): Observable<RagSessionDef[]> {
    return this.httpGet('sessions')
  }

  getSession(sessionId: string): Observable<RagTemplateDef> {
    return this.httpGet(`sessions/${sessionId}`)
  }

  getSessionChat(sessionId: string): Observable<ChatMessage[]> {
    return this.httpGet(`sessions/${sessionId}/chat`)
  }

  getSessionChatMessage(sessionId: string, messageId: string): Observable<ChatMessage> {
    return this.httpGet(`sessions/${sessionId}/chat/messages/${messageId}`)
  }

  postSessionChat(sessionId: string, message: ChatMessageRequest): Observable<ChatMessage> {
    return this.httpPost<ChatMessageRequest, ChatMessage>(`sessions/${sessionId}/chat`, message)
  }

  deleteSession(sessionId: string): Observable<PayloadBag<boolean>> {
    return this.httpDelete(`sessions/${sessionId}`)
  }

  createSession(item: RagSessionDef): Observable<PayloadBag<string>> {
    return this.httpPut<RagSessionDef, PayloadBag<string>>(
      `sessions`, item
    )
  }

  getTemplatesOverview(): Observable<OverviewMetrics> {
    return this.httpGet('templates/overview')
  }

  getTemplates(): Observable<RagTemplateDef[]> {
    return this.httpGet('templates')
  }

  filterTemplates(filter: RagFilterTemplate): Observable<RagTemplateDef[]> {
    return this.httpPost<RagFilterTemplate, RagTemplateDef[]>('templates', filter)
  }

  getTemplate(templateId: string): Observable<RagTemplateDef> {
    return this.httpGet(`templates/${templateId}`)
  }

  deleteTemplate(templateId: string): Observable<PayloadBag<boolean>> {
    return this.httpDelete(`templates/${templateId}`)
  }

  createTemplate(template: RagTemplateDef): Observable<PayloadBag<string>> {
    return this.httpPut<RagTemplateDef, PayloadBag<string>>(
      `templates`, template
    )
  }

  updateTemplate(template: RagTemplateDef): Observable<PayloadBag<string>> {
    return this.httpPost<RagTemplateDef, PayloadBag<string>>(
      `templates/${template.id}`, template
    )
  }

  getUserClaims(): Observable<string[]> {
    return this.httpGet('user-claims')
  }

  getUsersOverview(): Observable<OverviewMetrics> {
    return this.httpGet('users/overview')
  }

  getUsers(): Observable<UserListItem[]> {
    return this.httpGet('users')
  }

  getUser(uuid: string): Observable<UserDetails> {
    return this.httpGet(`users/${uuid}`)
  }

  createUser(data: UserCreateOrUpdate): Observable<PayloadBag<string>> {
    return this.httpPut<UserCreateOrUpdate, PayloadBag<string>>(
      `users`, data
    )
  }

  updateUser(data: UserCreateOrUpdate): Observable<PayloadBag<string>> {
    return this.httpPost<UserCreateOrUpdate, PayloadBag<string>>(
      `users/${data.id}`, data
    )
  }

  getTenantsOverview(): Observable<OverviewMetrics> {
    return this.httpGet('tenants/overview')
  }

  getTenants(): Observable<TenantListItem[]> {
    return this.httpGet('tenants')
  }

  getTenant(uuid: string): Observable<TenantListItem> {
    return this.httpGet(`tenants/${uuid}`)
  }

  exchange(state: string) {
    return this.httpPost<PayloadBag<string>, any>(
      'oauth2/google/sign-in/exchange', {
        data: state
      }
    )
  }
}
